// eslint-disable-next-line jira/filename-case
import { requestAccess as requestAccessExpansionService } from '@atlassian/expansion-services/requestAccess';
import {
	fireOperationalAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useRouterActions } from '@atlassian/react-resource-router';
import { useService } from '@jira/growth__jira-expansion-services/src/index.tsx';
import { useCallback, useRef } from 'react';
import { v4 as uuid } from 'uuid';
import type { AccessRequestType } from '../../common/types.tsx';
import { fireErrorAnalytics } from '../../services/analytics/index.tsx';
import {
	cjnEligibilityCacheHandler,
	increaseFeatureVisitedCount,
	setHasSeenNudgeInLast7Days,
	snoozeFeatureFor30Days,
} from '../../services/caching/index.tsx';
import { useFilteredUsersFromCollaborationGraph } from '../../services/get-collaborators/index.tsx';
import { useNudgeStateV2a } from '../../services/nudge-state/index.tsx';
import { useNudgeRecommendations } from '../../services/recommendations/index.tsx';
import { useRequestAccessErrorFlag } from '../request-access-error-flag/index.tsx';
import {
	getContextualBanditAnalyticsAttributes,
	getContextualBanditCJNMessageParser,
	useRequestAccessPromptFlag,
} from '../request-access-prompt-flag/index.tsx';
import { useRequestAccessSuccessFlag } from '../request-access-success-flag/index.tsx';

const MAX_NUMBER_OF_CONFLUENCE_COLLABORATORS = 5;

type UseConfluenceCrossJoinNudgeArguments = {
	featureVisitedCount: number;
};

export const useConfluenceCrossJoinNudge = (): (({
	featureVisitedCount,
}: UseConfluenceCrossJoinNudgeArguments) => Promise<void>) => {
	const sessionId = useRef(uuid());
	const getNudgeRecommendations = useNudgeRecommendations({ sessionId: sessionId.current });
	const cloudId = useCloudId();
	const getFilteredCollaborators = useFilteredUsersFromCollaborationGraph(
		cloudId,
		'confluence',
		MAX_NUMBER_OF_CONFLUENCE_COLLABORATORS,
	);
	const showRequestAccessPromptFlag = useRequestAccessPromptFlag();
	const showSuccessFlag = useRequestAccessSuccessFlag();
	const showErrorFlag = useRequestAccessErrorFlag();
	const { setNudgeDismissed: setNudgeDismissedV2a } = useNudgeStateV2a();
	const { push } = useRouterActions();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const userId = useAccountId() || '';

	const requestAccessService = useService(requestAccessExpansionService);

	return useCallback(
		async ({ featureVisitedCount }: UseConfluenceCrossJoinNudgeArguments) => {
			const startTimestamp = Date.now();
			try {
				// oxlint-disable-next-line no-inner-declarations
				async function onSubmit() {
					try {
						const serviceResult = await requestAccessService({
							product: 'confluence',
							integration: 'crossJoinNudgesConfluence',
							touchpoint: 'Crossjoin Nudge',
						});

						if (serviceResult.error) {
							fireErrorAnalytics('crossJoinNudgesConfluence', serviceResult.error);
							throw serviceResult.error;
						}

						const accessStatus: AccessRequestType = serviceResult.result;
						if (
							accessStatus === 'ACCESS_GRANTED' ||
							accessStatus === 'ACCESS_EXISTS' ||
							accessStatus === 'APPROVED_REQUEST_EXISTS'
						) {
							push('/wiki');
						} else if (
							accessStatus === 'PENDING_REQUEST_CREATED' ||
							accessStatus === 'PENDING_REQUEST_EXISTS'
						) {
							showSuccessFlag();
						} else {
							showErrorFlag('confluence');
						}
					} catch {
						showErrorFlag('confluence');
					}
				}

				const collaborators = await getFilteredCollaborators();
				const hasNoCollaborators = collaborators.length === 0;
				const entityId = hasNoCollaborators ? await getNudgeRecommendations() : undefined;

				const runPostDisplaySideEffects = () =>
					Promise.all([
						increaseFeatureVisitedCount(userId, featureVisitedCount + 1),
						cjnEligibilityCacheHandler.set('has_seen_conf', { value: true }),
						setHasSeenNudgeInLast7Days(),
					]);

				const fireSucceededOperationalAnalytics = () => {
					fireOperationalAnalytics(
						createAnalyticsEvent({}),
						'slo.crossJoinNudgesConfluence succeeded',
					);
				};

				const promptFlagArgs = {
					users: collaborators,
					onSubmit,
					onOptout: setNudgeDismissedV2a,
					snoozeFor30Days: () => snoozeFeatureFor30Days('crossJoinNudgesConfluence'),
					integration: 'crossJoinNudgesConfluence' as const,
					featureVisitedCount,
					messageParser: getContextualBanditCJNMessageParser({
						isContextualBandit: hasNoCollaborators,
						entityId: entityId || 'default',
						featureVisitedCount,
					}),
					eventAttributes: getContextualBanditAnalyticsAttributes({
						users: collaborators,
						userId,
						entityId: entityId || 'default',
						sessionId: sessionId.current,
						isContextualBandit: hasNoCollaborators,
						featureVisitedCount,
						startTimestamp,
					}),
				};

				const onDisplayed = () => {
					runPostDisplaySideEffects()
						.then(fireSucceededOperationalAnalytics)
						// oxlint-disable-next-line @typescript-eslint/no-explicit-any
						.catch((error: any) => {
							fireErrorAnalytics('useCrossJoinNudges', error, { cloudId });
						});
				};
				showRequestAccessPromptFlag({ ...promptFlagArgs, onDisplayed });
				// oxlint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				fireErrorAnalytics('useCrossJoinNudges', error, { cloudId });
			}
		},
		[
			cloudId,
			createAnalyticsEvent,
			getFilteredCollaborators,
			getNudgeRecommendations,
			push,
			requestAccessService,
			setNudgeDismissedV2a,
			showErrorFlag,
			showRequestAccessPromptFlag,
			showSuccessFlag,
			userId,
		],
	);
};
